<template>
  <div class="min-h-screen flex flex-col">
    <NavBar :title="' - Select Truck'" />

    <section class="flex-1 bg-gradient-to-br from-blue-500 to-slate-800 pt-16 pb-4">
      <div class="max-w-lg mx-auto px-4 py-4">
        <!-- QR Scanner -->
        <div v-if="!scanned" class="bg-white/95 backdrop-blur-lg rounded-2xl p-4 md:p-6 shadow-xl">
          <div class="text-center mb-6">
            <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl text-3xl text-white mb-4">
              <i class="fas fa-truck"></i>
            </div>
            <h2 class="text-xl font-semibold text-gray-800 mb-1">Scan Truck Code</h2>
            <p class="text-sm text-gray-500">Position the truck QR code within the frame</p>
          </div>
          <div class="relative rounded-xl overflow-hidden bg-black">
            <qr-stream @decode="onDecode">
              <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div class="w-48 h-48 border-3 border-white/80 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.5)] animate-pulse-border"></div>
              </div>
            </qr-stream>
          </div>
        </div>

        <!-- Scanned Result -->
        <div v-else class="space-y-4">
          <div class="bg-white/95 backdrop-blur-lg rounded-2xl p-4 md:p-6 shadow-xl text-center">
            <div class="flex flex-col items-center mb-4 pb-4 border-b border-gray-200">
              <div class="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-3xl mb-3 animate-scale-in">
                <i class="fas fa-check-circle"></i>
              </div>
              <h2 class="text-lg font-semibold text-gray-800">Truck Scanned</h2>
            </div>
            <div class="flex flex-col items-center p-4 bg-gray-100 rounded-xl">
              <span class="text-xs text-gray-500 uppercase tracking-wide mb-1">Truck Number</span>
              <span class="text-2xl font-bold text-gray-800 font-mono">{{ scannedTruckNumber }}</span>
            </div>
          </div>

          <!-- Error Message -->
          <transition name="error-fade">
            <div v-if="error" class="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <i class="fas fa-exclamation-triangle"></i>
              {{ error }}
            </div>
          </transition>

          <!-- Action Buttons -->
          <div class="flex flex-col gap-3">
            <button
              @click="confirmTruck"
              :disabled="isConfirmDisabled"
              class="w-full h-13 flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl shadow-lg shadow-green-500/30 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isLoading" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              <template v-else>
                <i class="fas fa-check"></i>
                <span>Confirm Truck</span>
              </template>
            </button>
            <button
              @click="rescan"
              class="w-full h-13 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all active:scale-[0.98]"
            >
              <i class="fas fa-redo"></i>
              <span>Scan Again</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <footer-bar></footer-bar>
  </div>
</template>

<script>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import NavBar from "@/components/NavBar.vue";
import { QrStream } from "vue3-qr-reader";
import { useStore } from "vuex";
import FooterBar from "@/components/FooterBar.vue";
import { authHeaders } from "@/composables/apiAuth";

export default {
  components: {
    NavBar,
    QrStream,
    FooterBar,
  },
  setup() {
    const scannedTruckNumber = ref("");
    const scanned = ref(false);
    const router = useRouter();
    const store = useStore();

    const isLoading = ref(false);
    const error = ref("");

    const isConfirmDisabled = computed(
      () => isLoading.value || error.value !== ""
    );

    function onDecode(data) {
      console.debug(data);
      scannedTruckNumber.value = data;
      scanned.value = true;
    }

    const confirmTruck = async () => {
      isLoading.value = true;
      error.value = "";

      const API_URL = store.state.api + "checkTruck";
      const userString = sessionStorage.getItem("user");
      const user = JSON.parse(userString);
      const siteId = user.siteid;

      const body = JSON.stringify({
        truckNumber: scannedTruckNumber.value,
        siteId: siteId,
      });

      console.debug(API_URL, body);

      const response = await fetch(API_URL, {
        method: "POST",
        headers: authHeaders(),
        body: body,
      });

      const data = await response.json();

      if (data[1]) {
        sessionStorage.setItem("truckNumber", scannedTruckNumber.value);
        router.push("/home");
      } else {
        error.value = "Invalid Truck Number";
        console.error("Invalid truck number or site ID.");
      }
    };

    const rescan = () => {
      scanned.value = false;
      scannedTruckNumber.value = "";
      error.value = "";
      isLoading.value = false;
    };

    return {
      scannedTruckNumber,
      scanned,
      confirmTruck,
      onDecode,
      rescan,
      isLoading,
      error,
      isConfirmDisabled,
    };
  },
};
</script>

<style scoped>
.h-13 {
  height: 3.25rem;
}

.border-3 {
  border-width: 3px;
}

@keyframes pulse-border {
  0%, 100% {
    border-color: rgba(255, 255, 255, 0.8);
  }
  50% {
    border-color: rgba(59, 130, 246, 1);
  }
}

.animate-pulse-border {
  animation: pulse-border 2s ease-in-out infinite;
}

.animate-scale-in {
  animation: scaleIn 0.4s ease;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.error-fade-enter-active,
.error-fade-leave-active {
  transition: all 0.3s ease;
}

.error-fade-enter-from,
.error-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
